import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShoppingCart, Package, Factory,
  Users, TrendingUp, AlertTriangle, ArrowRight, Truck,
  RefreshCw, BarChart2, Eye, EyeOff, CheckCircle, Clock, DollarSign, TrendingDown
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line, Legend } from "recharts";
import api from "../utils/api";
import { formatMXN, typeLabels, statusColors, statusLabels } from "../utils/format";

const PERIODS = [
  { key: "month", label: "Mes" },
  { key: "quarter", label: "Trimestre" },
  { key: "semester", label: "Semestre" },
  { key: "year", label: "Año" },
];

const MASKED = "••••••";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("month");
  const [refreshing, setRefreshing] = useState(false);
  const [hidden, setHidden] = useState(() => localStorage.getItem("dash_hidden") === "true");
  const navigate = useNavigate();

  const toggleHidden = () => {
    const next = !hidden;
    setHidden(next);
    localStorage.setItem("dash_hidden", String(next));
  };

  const mask = (val) => hidden ? MASKED : val;

  const load = async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    else setLoading(true);
    try {
      const r = await api.get(`/reports/dashboard?period=${period}`);
      setData(r.data);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const [prodStatus, setProdStatus] = useState(null);
  const [yieldTrends, setYieldTrends] = useState([]);
  const [yieldProduct, setYieldProduct] = useState("");

  const loadProdStatus = async () => {
    try {
      const r = await api.get("/dashboard/production-status");
      setProdStatus(r.data);
    } catch {}
  };

  const loadYieldTrends = async () => {
    try {
      const r = await api.get("/reports/yield-trends");
      setYieldTrends(r.data || []);
      if (r.data?.length > 0 && !yieldProduct) setYieldProduct(r.data[0].product_name);
    } catch {}
  };

  useEffect(() => { load(); }, [period]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadProdStatus(); loadYieldTrends(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Production costs detail
  const [showCostDetail, setShowCostDetail] = useState(false);
  const [costDetail, setCostDetail] = useState(null);
  const [loadingCosts, setLoadingCosts] = useState(false);

  const loadCostDetail = async () => {
    setLoadingCosts(true);
    setShowCostDetail(true);
    try {
      const r = await api.get(`/reports/production-costs?period=${period}`);
      setCostDetail(r.data);
    } catch { setCostDetail(null); }
    finally { setLoadingCosts(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="flex items-center gap-2 text-slate-400 text-sm">
        <RefreshCw size={16} className="animate-spin" /> Cargando dashboard...
      </div>
    </div>
  );

  const periodData = {
    sales: data?.period_bruto ?? 0,
    net: data?.period_neto ?? 0,
    shipping: data?.period_envios ?? 0,
    shipping_real: data?.period_envios_real ?? 0,
    shipping_diff: data?.period_envios_diferencia ?? 0,
    cobrado: data?.period_cobrado ?? 0,
    pedidos: data?.period_pedidos ?? 0,
    direct_costs: data?.period_direct_costs ?? 0,
    indirect_costs: data?.period_indirect_costs ?? 0,
    gross_profit: data?.period_gross_profit ?? 0,
    expenses_total: data?.period_expenses_total ?? 0,
    expenses_fijo: data?.period_expenses_fijo ?? 0,
    expenses_variable: data?.period_expenses_variable ?? 0,
    expenses_imprevisto: data?.period_expenses_imprevisto ?? 0,
    net_profit: data?.period_net_profit ?? 0,
  };

  // Use all-time data as fallback when current period has no data
  const allTime = data?.all_time;
  const showAllTime = periodData.pedidos === 0 && allTime && allTime.pedidos > 0;
  const displayData = showAllTime ? allTime : periodData;

  const pendiente = (displayData.bruto || displayData.sales || 0) - (displayData.cobrado || 0);
  const periodLabel = showAllTime ? "Total" : PERIODS.find((p) => p.key === period)?.label;
  const displaySales = displayData.bruto || displayData.sales || 0;
  const displayNet = displayData.neto || displayData.net || 0;

  const cards = [
    {
      label: `Vendido (${periodLabel})`,
      value: mask(formatMXN(displaySales)),
      sub: hidden ? null : `Neto: ${formatMXN(displayNet)}`,
      icon: TrendingUp, color: "text-emerald-600", bg: "bg-emerald-50",
      action: () => navigate("/ventas"),
    },
    {
      label: `Cobrado (${periodLabel})`,
      value: mask(formatMXN(displayData.cobrado || 0)),
      sub: hidden ? null : (pendiente > 0 ? `Pendiente: ${formatMXN(pendiente)}` : "Al corriente"),
      icon: CheckCircle,
      color: pendiente > 0 ? "text-amber-600" : "text-emerald-600",
      bg: pendiente > 0 ? "bg-amber-50" : "bg-emerald-50",
    },
    {
      label: `Costos Prod.`,
      value: mask(formatMXN((displayData.direct_costs || 0) + (displayData.indirect_costs || 0))),
      sub: hidden ? null : `Dir: ${formatMXN(displayData.direct_costs || 0)} · Ind: ${formatMXN(displayData.indirect_costs || 0)}`,
      icon: DollarSign, color: "text-red-600", bg: "bg-red-50",
      action: loadCostDetail,
    },
    {
      label: `Gastos Oper.`,
      value: mask(formatMXN(displayData.expenses_total || 0)),
      sub: hidden ? null : `Fijo: ${formatMXN(displayData.expenses_fijo || 0)} · Var: ${formatMXN(displayData.expenses_variable || 0)}`,
      icon: TrendingDown, color: "text-orange-600", bg: "bg-orange-50",
      action: () => navigate("/gastos"),
    },
  ];

  const cards1b = [
    {
      label: `Ganancia Bruta`,
      value: mask(formatMXN(displayData.gross_profit || 0)),
      sub: hidden ? null : (displayNet > 0 ? `Margen: ${(((displayData.gross_profit || 0) / displayNet) * 100).toFixed(1)}%` : ""),
      icon: TrendingUp,
      color: (displayData.gross_profit || 0) > 0 ? "text-emerald-700" : "text-red-700",
      bg: (displayData.gross_profit || 0) > 0 ? "bg-emerald-50" : "bg-red-50",
    },
    {
      label: `Utilidad Neta`,
      value: mask(formatMXN(displayData.net_profit || 0)),
      sub: hidden ? null : (displayNet > 0 ? `Margen neto: ${(((displayData.net_profit || 0) / displayNet) * 100).toFixed(1)}%` : ""),
      icon: TrendingUp,
      color: (displayData.net_profit || 0) > 0 ? "text-brand-700" : "text-red-700",
      bg: (displayData.net_profit || 0) > 0 ? "bg-brand-50" : "bg-red-50",
    },
    {
      label: `Envio Cobrado`,
      value: mask(formatMXN(displayData.shipping || displayData.envios || 0)),
      sub: hidden ? null : `Real: ${formatMXN(displayData.shipping_real || displayData.envios_real || 0)}`,
      icon: Truck, color: "text-violet-600", bg: "bg-violet-50",
    },
    {
      label: `Dif. Envios`,
      value: mask(formatMXN(displayData.shipping_diff || displayData.envios_diferencia || 0)),
      sub: hidden ? null : ((displayData.shipping_diff || displayData.envios_diferencia || 0) >= 0 ? "A favor" : "En contra"),
      icon: Truck,
      color: (displayData.shipping_diff || displayData.envios_diferencia || 0) >= 0 ? "text-emerald-600" : "text-red-600",
      bg: (displayData.shipping_diff || displayData.envios_diferencia || 0) >= 0 ? "bg-emerald-50" : "bg-red-50",
    },
  ];

  const cards2 = [
    {
      label: `Pedidos (${periodLabel})`,
      value: displayData.pedidos || periodData.pedidos,
      sub: "documentos registrados",
      icon: ShoppingCart, color: "text-blue-600", bg: "bg-blue-50",
      action: () => navigate("/ventas"),
    },
    {
      label: "Clientes",
      value: data?.total_customers ?? 0,
      sub: "registrados en CRM",
      icon: Users, color: "text-violet-600", bg: "bg-violet-50",
      action: () => navigate("/clientes"),
    },
    {
      label: "Produccion activa",
      value: (prodStatus?.in_oven || 0) + (prodStatus?.cooling || 0) + (prodStatus?.scheduled || 0),
      sub: `${prodStatus?.in_oven || 0} en horno · ${prodStatus?.cooling || 0} enfriando`,
      icon: Factory, color: "text-amber-600", bg: "bg-amber-50",
      action: () => navigate("/fabricacion"),
    },
    {
      label: "Pedidos pendientes",
      value: prodStatus?.pending_orders || 0,
      sub: "por producir y enviar",
      icon: Clock, color: "text-rose-600", bg: "bg-rose-50",
      action: () => navigate("/planeacion"),
    },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 text-sm mt-1">Resumen general de tu negocio</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Period filter */}
          <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                data-testid={`period-${p.key}`}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                  period === p.key
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {/* Hide/Show balances */}
          <button
            onClick={toggleHidden}
            data-testid="toggle-balances"
            title={hidden ? "Mostrar saldos" : "Ocultar saldos"}
            className="p-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors"
          >
            {hidden
              ? <EyeOff size={14} className="text-slate-500" />
              : <Eye size={14} className="text-slate-500" />
            }
          </button>
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            className="p-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors"
            title="Actualizar"
          >
            <RefreshCw size={14} className={`text-slate-500 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* All-time fallback banner */}
      {showAllTime && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5 text-sm text-blue-700 flex items-center justify-between">
          <span>Sin ventas en el periodo seleccionado. Mostrando totales historicos ({allTime.pedidos} ventas).</span>
          <button onClick={() => setPeriod("year")} className="text-xs font-medium underline ml-2">Ver Anual</button>
        </div>
      )}

      {/* KPI Cards - Financial Row 1 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div
            key={c.label}
            onClick={c.action}
            className={`bg-white rounded-xl border border-slate-200 p-5 shadow-sm transition-all ${c.action ? "cursor-pointer hover:shadow-md hover:border-slate-300" : ""}`}
            data-testid={`kpi-${c.label.split(" ")[0].toLowerCase()}`}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{c.label}</div>
                <div className="text-2xl font-bold text-slate-900">{c.value}</div>
                {c.sub && <div className={`text-xs mt-1 ${c.sub?.includes("Pendiente") ? "text-amber-600 font-medium" : "text-slate-400"}`}>{c.sub}</div>}
              </div>
              <div className={`${c.bg} p-2.5 rounded-lg flex-shrink-0`}>
                <c.icon size={20} className={c.color} />
              </div>
            </div>
            {c.action && (
              <div className="mt-3 flex items-center gap-1 text-xs text-slate-400 font-medium">
                Ver detalle <ArrowRight size={11} />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* KPI Cards - Financial Row 2: Profits & Shipping */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards1b.map((c) => (
          <div
            key={c.label}
            onClick={c.action}
            className={`bg-white rounded-xl border border-slate-200 p-5 shadow-sm transition-all ${c.action ? "cursor-pointer hover:shadow-md hover:border-slate-300" : ""}`}
            data-testid={`kpi-${c.label.split(" ")[0].toLowerCase()}`}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{c.label}</div>
                <div className="text-2xl font-bold text-slate-900">{c.value}</div>
                {c.sub && <div className="text-xs mt-1 text-slate-400">{c.sub}</div>}
              </div>
              <div className={`${c.bg} p-2.5 rounded-lg flex-shrink-0`}>
                <c.icon size={20} className={c.color} />
              </div>
            </div>
            {c.action && (
              <div className="mt-3 flex items-center gap-1 text-xs text-slate-400 font-medium">
                Ver detalle <ArrowRight size={11} />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* KPI Cards - Secondary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards2.map((c) => (
          <div
            key={c.label}
            onClick={c.action}
            className={`bg-white rounded-xl border border-slate-200 p-4 shadow-sm transition-all ${c.action ? "cursor-pointer hover:shadow-md hover:border-slate-300" : ""}`}
          >
            <div className="flex items-center gap-3">
              <div className={`${c.bg} p-2 rounded-lg flex-shrink-0`}>
                <c.icon size={16} className={c.color} />
              </div>
              <div>
                <div className="text-lg font-bold text-slate-900">{c.value}</div>
                <div className="text-xs text-slate-500">{c.label} · {c.sub}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Cobrado vs Pendiente bar */}
      {!hidden && (periodData.sales) > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800 text-sm">Cobrado vs Pendiente ({periodLabel})</h3>
            <div className="flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" />Cobrado: {formatMXN(periodData.cobrado)}</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block" />Pendiente: {formatMXN(pendiente > 0 ? pendiente : 0)}</span>
            </div>
          </div>
          <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(100, ((periodData.cobrado) / (periodData.sales || 1)) * 100)}%` }}
            />
          </div>
          <div className="mt-1.5 text-xs text-slate-400">
            {Math.round(((periodData.cobrado) / (periodData.sales || 1)) * 100)}% cobrado del total facturado
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sales Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <BarChart2 size={16} className="text-slate-400" /> Ventas vs Costos ({periodLabel})
              </h3>
              <p className="text-xs text-slate-500">Verde = Ganancia, Rojo = Costos, Ámbar = Envíos</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={hidden ? [] : (data?.monthly_chart || [])} barSize={20}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false}
                tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
              <Tooltip
                formatter={(v, name) => {
                  const labels = { ganancia: "Ganancia", costos: "Costos prod.", envios: "Envíos" };
                  return [formatMXN(v), labels[name] || name];
                }}
                contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 }}
              />
              <Bar dataKey="ganancia" stackId="a" fill="#059669" radius={[0, 0, 0, 0]} name="ganancia" />
              <Bar dataKey="costos" stackId="a" fill="#dc2626" radius={[0, 0, 0, 0]} name="costos" />
              <Bar dataKey="envios" stackId="a" fill="#f59e0b" radius={[4, 4, 0, 0]} name="envios" />
            </BarChart>
          </ResponsiveContainer>
          {hidden && (
            <div className="flex items-center justify-center h-20 text-slate-300 text-sm gap-2">
              <EyeOff size={16} /> Saldos ocultos
            </div>
          )}
        </div>

        {/* Right column */}
        <div className="space-y-4">
          {/* Production quick view */}
          {prodStatus && prodStatus.active_batches?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900 text-sm flex items-center gap-1.5">
                  <Factory size={14} className="text-amber-500" /> En produccion
                </h3>
                <button onClick={() => navigate("/fabricacion")}
                  className="text-xs text-brand-600 hover:underline flex items-center gap-1">
                  Ver todo <ArrowRight size={10} />
                </button>
              </div>
              <div className="space-y-2">
                {prodStatus.active_batches.slice(0, 3).map((b, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                    <div>
                      <div className="text-xs font-medium text-slate-800">{b.batch_number}</div>
                      <div className="text-xs text-slate-500">{b.product_name}</div>
                    </div>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                      b.status === "en_horno" ? "bg-amber-100 text-amber-700" :
                      b.status === "enfriando" ? "bg-cyan-100 text-cyan-700" :
                      "bg-blue-100 text-blue-700"
                    }`}>
                      {b.status === "en_horno" ? "En horno" : b.status === "enfriando" ? "Enfriando" : "Programado"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Low stock alert */}
          {prodStatus?.low_stock_products?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={16} className="text-amber-600" />
                <span className="font-semibold text-amber-800 text-sm">Stock bajo</span>
              </div>
              <div className="space-y-1.5">
                {prodStatus.low_stock_products.slice(0, 5).map((p, i) => (
                  <div key={i} className="flex justify-between text-xs">
                    <span className="text-amber-800">{p.name}</span>
                    <span className="font-bold text-amber-900">{p.stock} {p.unit || "kg"}</span>
                  </div>
                ))}
              </div>
              <button onClick={() => navigate("/inventario")}
                className="mt-2 text-xs text-amber-700 font-medium flex items-center gap-1 hover:underline">
                Ver inventario <ArrowRight size={12} />
              </button>
            </div>
          )}

          {/* Stock alert - legacy */}
          {!prodStatus?.low_stock_products?.length && data?.low_stock_count > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle size={16} className="text-amber-600" />
                <span className="font-semibold text-amber-800 text-sm">Stock bajo</span>
              </div>
              <div className="text-2xl font-bold text-amber-900">{data.low_stock_count}</div>
              <div className="text-xs text-amber-700">productos con stock mínimo</div>
              <button onClick={() => navigate("/inventario")}
                className="mt-2 text-xs text-amber-700 font-medium flex items-center gap-1 hover:underline">
                Ver inventario <ArrowRight size={12} />
              </button>
            </div>
          )}

          {/* Top products */}
          {data?.top_products?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900 text-sm">Top productos</h3>
                <button onClick={() => navigate("/reportes")}
                  className="text-xs text-brand-600 hover:underline flex items-center gap-1">
                  Ver reportes <ArrowRight size={10} />
                </button>
              </div>
              <div className="space-y-2">
                {data.top_products.slice(0, 4).map((p, i) => (
                  <div key={p.product_id || i} className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-brand-50 text-brand-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-slate-800 truncate">{p.name}</div>
                      <div className="text-xs text-slate-400">
                        {hidden ? MASKED : `${p.total_qty} kg · ${formatMXN(p.total_revenue)}`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent sales */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-900 text-sm">Actividad reciente</h3>
              <button onClick={() => navigate("/ventas")}
                className="text-xs text-brand-600 hover:underline flex items-center gap-1">
                Ver todas <ArrowRight size={10} />
              </button>
            </div>
            <div className="space-y-2">
              {(data?.recent_sales || []).slice(0, 4).map((s) => (
                <div key={s.id} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                  <div>
                    <div className="text-xs font-medium text-slate-800">{s.folio}</div>
                    <div className="text-xs text-slate-500 truncate max-w-[110px]">{s.customer_name || "Sin cliente"}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-semibold text-slate-900">{mask(formatMXN(s.total))}</div>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${statusColors[s.status] || "bg-slate-100 text-slate-600"}`}>
                      {statusLabels[s.status] || s.status}
                    </span>
                  </div>
                </div>
              ))}
              {(!data?.recent_sales || data.recent_sales.length === 0) && (
                <p className="text-xs text-slate-400">Sin actividad reciente</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Yield Trend Chart */}
      {yieldTrends.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-testid="yield-trend-chart">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <TrendingUp size={16} className="text-slate-400" /> Tendencia de Rendimiento
              </h3>
              <p className="text-xs text-slate-500">Rendimiento global (%) por lote completado</p>
            </div>
            <select
              value={yieldProduct}
              onChange={(e) => setYieldProduct(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-700"
              data-testid="yield-product-select"
            >
              <option value="">Todos los productos</option>
              {yieldTrends.map((t) => (
                <option key={t.product_name} value={t.product_name}>{t.product_name}</option>
              ))}
            </select>
          </div>
          {(() => {
            const COLORS = ["#059669", "#2563eb", "#d97706", "#dc2626", "#7c3aed", "#0891b2", "#be185d", "#65a30d"];
            const filtered = yieldProduct
              ? yieldTrends.filter((t) => t.product_name === yieldProduct)
              : yieldTrends;

            if (yieldProduct && filtered.length > 0) {
              const pts = filtered[0].data_points || [];
              const chartData = pts.map((p, i) => ({
                label: p.date || `#${i + 1}`,
                rendimiento: p.global_yield,
              }));
              return (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false}
                      tickFormatter={(v) => `${v}%`} />
                    <Tooltip formatter={(v) => [`${v}%`, "Rendimiento"]}
                      contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 }} />
                    <Line type="monotone" dataKey="rendimiento" stroke="#059669" strokeWidth={2}
                      dot={{ fill: "#059669", r: 4 }} activeDot={{ r: 6 }} name="Rendimiento %" />
                  </LineChart>
                </ResponsiveContainer>
              );
            }

            // Multi-product: aggregate by date, one line per product
            const allDates = new Set();
            filtered.forEach((t) => (t.data_points || []).forEach((p) => allDates.add(p.date)));
            const sortedDates = [...allDates].sort();
            const chartData = sortedDates.map((d) => {
              const row = { date: d };
              filtered.forEach((t) => {
                const pt = (t.data_points || []).find((p) => p.date === d);
                if (pt) row[t.product_name] = pt.global_yield;
              });
              return row;
            });
            return (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false}
                    tickFormatter={(v) => `${v}%`} />
                  <Tooltip formatter={(v, name) => [`${v}%`, name]}
                    contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {filtered.map((t, i) => (
                    <Line key={t.product_name} type="monotone" dataKey={t.product_name}
                      stroke={COLORS[i % COLORS.length]} strokeWidth={2}
                      dot={{ fill: COLORS[i % COLORS.length], r: 3 }} connectNulls />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            );
          })()}
          {hidden && (
            <div className="flex items-center justify-center h-20 text-slate-300 text-sm gap-2">
              <EyeOff size={16} /> Saldos ocultos
            </div>
          )}
        </div>
      )}

      {/* Production Costs Detail Modal */}
      {showCostDetail && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-16 px-4" onClick={() => setShowCostDetail(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[75vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="cost-detail-modal">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Costos de Produccion</h2>
                <p className="text-xs text-slate-400">Periodo: {PERIODS.find(p => p.key === period)?.label || period}</p>
              </div>
              <button onClick={() => setShowCostDetail(false)} className="text-slate-400 hover:text-slate-600 text-xl font-bold px-2">&times;</button>
            </div>

            {loadingCosts ? (
              <div className="p-12 text-center text-slate-400">Cargando...</div>
            ) : !costDetail ? (
              <div className="p-12 text-center text-slate-400">Sin datos de costos</div>
            ) : (
              <div className="p-6 space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="text-[10px] font-bold text-red-500 uppercase">Materia Prima</div>
                    <div className="text-lg font-bold text-red-700">{formatMXN(costDetail.totals?.raw_material || 0)}</div>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <div className="text-[10px] font-bold text-amber-500 uppercase">Produccion</div>
                    <div className="text-lg font-bold text-amber-700">{formatMXN(costDetail.totals?.production || 0)}</div>
                  </div>
                  <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
                    <div className="text-[10px] font-bold text-teal-500 uppercase">Gas</div>
                    <div className="text-lg font-bold text-teal-700">{formatMXN(costDetail.totals?.gas_cylinders || 0)}</div>
                  </div>
                  <div className="bg-slate-100 border border-slate-300 rounded-lg p-3">
                    <div className="text-[10px] font-bold text-slate-500 uppercase">Total</div>
                    <div className="text-lg font-bold text-slate-900">{formatMXN(costDetail.totals?.total || 0)}</div>
                    <div className="text-[10px] text-slate-400">{costDetail.totals?.batches_count || 0} lotes · {costDetail.totals?.total_output_kg || 0} kg</div>
                  </div>
                </div>

                {/* Cost by Concept */}
                {costDetail.cost_by_concept?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-bold text-slate-700 mb-2">Costos por Concepto</h3>
                    <div className="space-y-1">
                      {costDetail.cost_by_concept.map((c, i) => (
                        <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                          <span className="text-sm text-slate-700">{c.name}</span>
                          <span className="text-sm font-bold text-slate-900">{formatMXN(c.total)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Per-Product Costs */}
                {costDetail.product_costs?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-bold text-slate-700 mb-2">Costo por Producto</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="text-left px-3 py-2 font-semibold text-slate-500">Producto</th>
                            <th className="text-right px-3 py-2 font-semibold text-slate-500">Directo/kg</th>
                            <th className="text-right px-3 py-2 font-semibold text-slate-500">Indirecto/kg</th>
                            <th className="text-right px-3 py-2 font-semibold text-slate-500">Total/kg</th>
                            <th className="text-right px-3 py-2 font-semibold text-slate-500">Tendencia</th>
                            <th className="text-right px-3 py-2 font-semibold text-slate-500">Stock</th>
                            <th className="text-right px-3 py-2 font-semibold text-slate-500">Valor Inv.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {costDetail.product_costs.map((p, i) => {
                            const t = p.trend;
                            const isUp = t && t.change > 0;
                            const isDown = t && t.change < 0;
                            return (
                            <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="px-3 py-2 font-medium text-slate-800">{p.product_name}</td>
                              <td className="px-3 py-2 text-right text-red-600">{formatMXN(p.direct_per_kg)}</td>
                              <td className="px-3 py-2 text-right text-amber-600">{formatMXN(p.indirect_per_kg)}</td>
                              <td className="px-3 py-2 text-right font-bold text-slate-900">{formatMXN(p.total_per_kg)}</td>
                              <td className="px-3 py-2 text-right">
                                {t && t.previous > 0 ? (
                                  <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                                    isUp ? "bg-red-100 text-red-700" : isDown ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
                                  }`}>
                                    {isUp ? "▲" : isDown ? "▼" : "="} {Math.abs(t.change_pct)}%
                                  </span>
                                ) : (
                                  <span className="text-[10px] text-slate-300">—</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right text-slate-600">{p.stock} {p.unit}</td>
                              <td className="px-3 py-2 text-right font-bold text-slate-700">{formatMXN(p.inventory_value)}</td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Batch Details */}
                {costDetail.batch_details?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-bold text-slate-700 mb-2">Lotes Completados</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="text-left px-3 py-2 font-semibold text-slate-500">Lote</th>
                            <th className="text-left px-3 py-2 font-semibold text-slate-500">Producto</th>
                            <th className="text-left px-3 py-2 font-semibold text-slate-500">Fecha</th>
                            <th className="text-right px-3 py-2 font-semibold text-slate-500">Salida</th>
                            <th className="text-right px-3 py-2 font-semibold text-slate-500">M. Prima</th>
                            <th className="text-right px-3 py-2 font-semibold text-slate-500">Produccion</th>
                            <th className="text-right px-3 py-2 font-semibold text-slate-500">Total</th>
                            <th className="text-right px-3 py-2 font-semibold text-slate-500">$/kg</th>
                          </tr>
                        </thead>
                        <tbody>
                          {costDetail.batch_details.map((b, i) => {
                            const isUp = b.trend_change > 0;
                            const isDown = b.trend_change < 0;
                            return (
                            <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="px-3 py-2 font-mono text-slate-600">{b.batch_number}</td>
                              <td className="px-3 py-2 font-medium text-slate-800">{b.product_name}</td>
                              <td className="px-3 py-2 text-slate-500">{b.completed_at}</td>
                              <td className="px-3 py-2 text-right text-slate-700">{b.output_kg} kg</td>
                              <td className="px-3 py-2 text-right text-red-600">{formatMXN(b.raw_material_cost)}</td>
                              <td className="px-3 py-2 text-right text-amber-600">{formatMXN(b.production_cost)}</td>
                              <td className="px-3 py-2 text-right font-bold text-slate-900">{formatMXN(b.total_cost)}</td>
                              <td className="px-3 py-2 text-right">
                                <span className="font-bold text-brand-700">{formatMXN(b.cost_per_kg)}</span>
                                {b.trend_pct !== 0 && (
                                  <span className={`ml-1 text-[9px] font-bold ${isUp ? "text-red-600" : "text-emerald-600"}`}>
                                    {isUp ? "▲" : "▼"}{Math.abs(b.trend_pct)}%
                                  </span>
                                )}
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Empty state */}
                {(!costDetail.batch_details?.length && !costDetail.product_costs?.length) && (
                  <div className="text-center py-8">
                    <DollarSign size={32} className="mx-auto text-slate-300 mb-2" />
                    <p className="text-slate-400 text-sm">Sin datos de costos para este periodo.</p>
                    <p className="text-slate-400 text-xs mt-1">Los costos se alimentan al completar lotes de produccion con costos reales en Fabricacion.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
